/**
 * Pure rules for tse:diag_state on the client (plan 15.3, 16.3; tse_diag_state.schema.json).
 *
 * Ordering: events are ordered by the pair (diagnosis_id, seq). Within one diagnosis_id any seq <= the
 * last one rendered is dropped (a revive re-sends the stored state with the SAME seq, which is then a
 * no-op). A different diagnosis_id is a new diagnosis and its first event is accepted; a diagnosis_id
 * that was already replaced in this session is retired and never comes back.
 */

export const DIAG_STATE_SCHEMA_VERSION = 1;
export const BADGE_PCT_THRESHOLD = 1;
const RETIRED_LIMIT = 32;

export const DECISION = Object.freeze({
  NEW_DIAGNOSIS: "new_diagnosis",
  ACCEPT: "accept",
  STALE: "stale",
  RETIRED: "retired",
  INVALID: "invalid",
});

/** Only the fields the client relies on; the server validates the full schema before emitting. */
export function isDiagStateShape(payload) {
  return !!payload
    && typeof payload === "object"
    && typeof payload.session_id === "string" && payload.session_id.length > 0
    && typeof payload.diagnosis_id === "string" && payload.diagnosis_id.length > 0
    && Number.isInteger(payload.seq) && payload.seq >= 1
    && payload.schema_version === DIAG_STATE_SCHEMA_VERSION
    && Array.isArray(payload.causes);
}

/**
 * @param {{state: object|null, retired: string[]}|undefined} entry what is stored for the payload's session
 * @param {object} payload incoming tse:diag_state
 * @returns {string} one of DECISION
 */
export function decideDiagState(entry, payload) {
  if (!isDiagStateShape(payload)) return DECISION.INVALID;
  const current = entry?.state || null;
  if ((entry?.retired || []).includes(payload.diagnosis_id)) return DECISION.RETIRED;
  if (!current || current.diagnosis_id !== payload.diagnosis_id) return DECISION.NEW_DIAGNOSIS;
  return payload.seq > current.seq ? DECISION.ACCEPT : DECISION.STALE;
}

/** The next stored entry for a session after `decision` (unchanged entry for drops). */
export function nextEntry(entry, payload, decision) {
  const base = entry || { state: null, retired: [], diagnosisCount: 0 };
  if (decision === DECISION.ACCEPT) return { ...base, state: payload };
  if (decision !== DECISION.NEW_DIAGNOSIS) return base;
  const retired = base.state ? [...base.retired, base.state.diagnosis_id].slice(-RETIRED_LIMIT) : base.retired;
  return { state: payload, retired, diagnosisCount: (base.diagnosisCount || 0) + 1 };
}

const isOpen = (cause) => cause && cause.status !== "ruled_out" && cause.status !== "failed";

/** The cause shown first: the first open cause in the server's order. */
export function topCause(state) {
  return (state?.causes || []).find(isOpen) || null;
}

/**
 * Whether a new state deserves a bubble badge (15.4): the top cause moved by >= 1 point (or another
 * cause took the top), or a question is waiting that was not waiting before.
 */
export function bubbleSignal(prev, next) {
  if (!next) return { pctMoved: false, newPending: false };
  const before = prev && prev.diagnosis_id === next.diagnosis_id ? prev : null;
  const a = topCause(before);
  const b = topCause(next);
  let pctMoved = false;
  if (b) {
    if (!a || a.key !== b.key) pctMoved = !!a;
    else pctMoved = Math.abs((b.percentage || 0) - (a.percentage || 0)) >= BADGE_PCT_THRESHOLD;
  }
  const pendingId = next.pending?.question_id || null;
  const newPending = !!pendingId && pendingId !== (before?.pending?.question_id || null);
  return { pctMoved, newPending };
}

export const isEnded = (state) => state?.lifecycle === "ended";
